<script>
// Components
import appConfig from "@/app.config.json";
import Layout from "@/router/layouts/main";
import PageHeader from "@/components/page-header";
import UserProfile from "@/router/views/users/user-profile"
import ModalWriteUser from "@/components/widgets/ModalWriteUser";

// Helpers
import {authComputed} from "@/state/helpers";
import UserUtils from '@/helpers/users';
import DateUtils from '@/helpers/date';
import Storage from "@/helpers/storage";
import firestoreUtils from "@/helpers/firestore";

// Services
import TicketsDataService from "@/services/TicketsDataService";
import UsersDataService from "@/services/UsersDataService";
import AnnotationsService from "@/services/AnnotationsService";
import MailService from "@/services/MailService";

// External Components
import CoolLightBox from 'vue-cool-lightbox';
import 'vue-cool-lightbox/dist/vue-cool-lightbox.min.css';
import countriesISO from "i18n-iso-countries";
import {VueTabs, VTab} from 'vue-nav-tabs'
import 'vue-nav-tabs/themes/vue-tabs.css';

// Libs
import isEmpty from "is-empty";

export default {
  name: "ticketDetails",
  page: {
    localePicker: () => import(`vue2-datepicker/locale/${this.$i18n.locale}`),
    title: "Tickets Manager",
    meta: [
      {
        name: "description",
        content: appConfig.description
      },
    ],
  },
  components: {
    Layout,
    PageHeader,
    ModalWriteUser,
    VueTabs,
    VTab,
    UserProfile,
    CoolLightBox,
  },
  computed: {
    ...authComputed,

    getCountry() {
      return UserUtils.getCountryName(countriesISO, this.user.country.toUpperCase(), this.$i18n.locale)
    }
  },
  methods: {
    ...UserUtils,
    ...DateUtils,

    handlePageChange(value) {
      this.page = value;
      this.loadHistory();
    },

    loadTicket() {
      this.isLoading = true;
      const params = {
        adminId: this.getUser.uid
      };

      TicketsDataService
        .review(this.ticketId, params)
        .then(async response => {
          this.ticket = response.data.ticket;
          this.lastMessage = response.data.messages.list.shift();
          this.history = response.data.messages.list;
          this.events = response.data.messages.events;
          this.attachments = response.data.attachments;

          // user
          if (this.ticket.userId != null)
            await this.loadUser(this.ticket.userId);
          else
            await this.loadUserByEmail(this.ticket.email);

          // device
          this.device = JSON.parse(this.ticket.device);

          // attachments
          if (this.attachments.length > 0) {
            this.attachments.forEach(attachment => {
              Storage.getTicketMediaURL(this.ticketId, attachment.fileName).then((imageUrl) => {
                document.getElementById('attachment-' + attachment.fileName).setAttribute('src', imageUrl);
                this.attachmentsMedia.media.push({
                  src: imageUrl,
                  title: attachment.fileName
                })
              });
            });
          }

          this.isLoading = false;
        })
        .catch(error => {
          window.console.log(error);
          this.$toast.error("ERROR: " + error.toString());
          this.isLoading = false;
        });
    },

    async closeTicket() {
      const params = {
        adminId: this.getUser.uid
      };
      TicketsDataService.close(this.ticketId, params)
        .then(() => {
          this.$toast.success(this.$t("tickets.close_success"));
          this.$router.push("/support/tickets");
        })
        .catch((error) => {
          window.console.error(error);
          this.$toast.error(error.toString());
        });
    },

    replyTicket() {
      this.callModal();
    },

    async sendReply(message, sendAs) {
      this.replyMessage = message;
      if (isEmpty(message)) {
        this.$toast.error("Message is empty");
        return;
      }

      if (!sendAs.app && !sendAs.mail) {
        this.$toast.error("Select one sending method");
        return;
      }

      this.isSending = true;
      const result = {
        app: {
          status: false
        },
        mail: {
          status: false
        }
      }
      try {
        const response = await TicketsDataService.sendReply(this.ticketId, {
          adminId: this.getUser.uid,
          message: message,
        });

        const newMessageId = response.data.messageId;

        if (sendAs.app) {
          try {
            result.app = await firestoreUtils.sendMessage(this.user.userId, message);
            if (result.app.status === false) {
              this.$toast.error(result.app.error);
            }
          } catch (error) {
            window.console.error("error sendAs.app");
            window.console.error(error);
            this.$toast.error("Error sending APP message: " + error.message);
          }
        }

        if (sendAs.mail) {
          const mailParams = {
            adminId: this.getUser.uid,
            userEmail: this.user.email,
            type: "ticket",
            language: this.ticket.language,
            message: message,
            originalId: this.ticketId,
            userId: this.user.userId,
            originalMessageId: newMessageId,
          };

          if (!isEmpty(this.user.userId)) {
            mailParams.userName = this.user.name;
          }

          let messageResult;
          try {
            messageResult = await MailService.generateMessage(mailParams);
            if (messageResult.data.status === true) {
              mailParams.messageUrl = "https://spreedl.com/messages/read/" + newMessageId + "/" + messageResult.data.generateId;
              const resultMail = await MailService.sendMail(mailParams);
              result.mail = resultMail.data;
              if (result.mail.status === false) {
                this.$toast.error(result.mail.error);
                await MailService.deleteGenerateMessage({generateId: messageResult.data.generateId});
              }
            } else {
              this.$toast.error(messageResult.data.error);
            }
          } catch (error) {
            window.console.error("error sendAs.mail");
            window.console.error(error);
            this.$toast.error("ERROR sending email message: " + error.message);
            if (!isEmpty(messageResult.data.generateId)) {
              await MailService.deleteGenerateMessage({generateId: messageResult.data.generateId});
            }
          }
        }

        if ((result.app.status || result.mail.status)) {
          this.replyMessage = "";
          if (!isEmpty(this.user.userId)) {
            try {
              let annotationStr = "Message (" + (result.app.status ? 'APP' : '') + " " + (result.mail.status ? 'Email' : '') + ") send: " + message.replace('/\\r?\\n|\\r/g', ' ');
              const annotation = {
                adminId: this.getUser.uid,
                message: annotationStr
              };
              await AnnotationsService.saveAnnotation(this.user.userId, annotation);
            } catch (error) {
              window.console.error("addAnnotation", error);
              this.$toast.warning("Message send but annotation not inserted " + error.message);
            }
          }

          this.isShowModal = !(sendAs.mail === result.mail.status && sendAs.app === result.app.status);
        }

        this.isSending = false;
        if (!this.isShowModal) {
          this.loadTicket();
        }
      } catch (error) {
        this.isSending = false;
        window.console.error(error);
        this.$toast.error("GLOBAL ERROR "+ error.message);
      }
    },

    async loadUserByEmail(email) {
      if (email == null || email === '')
        return;

      try {
        const result = await UsersDataService.getByEmail({
          email: email
        });
        if (typeof result.data === 'undefined')
          throw new Error('unable to find user ' + email);

        if (isEmpty(result.userId)) {
          this.user = this.createDefaultUser();
          return;
        }

        this.user = result.data;
        Storage.getUserMediaUrl(this.user.userId, this.user.photo)
            .then(imageUrl => {
              document.getElementById('ticket-userImg')
                  .setAttribute("src", imageUrl);
            });
      } catch (error) {
        window.console.error(error);
        this.$toast.error(this.$t("global_error"));
        this.user = this.createDefaultUser();
      }
    },

    async loadUser(userId) {
      if (userId == null || userId === '')
        return;

      try {
        const result = await UsersDataService.get(userId);
        if (typeof result.data === 'undefined')
          throw new Error('unable to find user ' + userId);

        if (isEmpty(result.data.userId)) {
          this.user = this.createDefaultUser();
          return;
        }

        this.user = result.data;
        if (!isEmpty(this.user.photo)) {
          Storage.getUserMediaUrl(this.user.userId, this.user.photo)
              .then(imageUrl => {
                document.getElementById('ticket-userImg')
                    .setAttribute("src", imageUrl);
              });

          Storage.getUserMediaUrl(this.user.userId, this.user.photo.replace("th_", ""))
              .then((imageUrl) => {
                this.attachmentsMedia.media.push({
                  src: imageUrl,
                  title: this.user.userId
                })
              });
        }

      } catch (error) {
        window.console.error(error);
        this.$toast.error(this.$t("global_error"));
        this.user = this.createDefaultUser();
      }
    },

    createDefaultUser() {
      return {
        userId: '',
        originalUserId: this.ticket.userId,
        name: this.$t("user.unknown"),
        email: this.ticket.email
      }
    },

    getRequestParams(page, pageSize) {
      let params = {};

      if (page) {
        params["page"] = page - 1;
      }

      if (pageSize) {
        params["size"] = pageSize;
      }

      return params;
    },

    showAttachment(attachmentId) {
      const index = this.attachmentsMedia.media.findIndex(x => x.title === attachmentId);
      this.attachmentsMedia.index = index;
    },

    getContactTypeIcon(type) {
      return require(`@/assets/images/support_type_${type}.png`)
    },

    formatSizeUnits(bytes) {
      if      (bytes >= 1073741824) { bytes = (bytes / 1073741824).toFixed(2) + " GB"; }
      else if (bytes >= 1048576)    { bytes = (bytes / 1048576).toFixed(2) + " MB"; }
      else if (bytes >= 1024)       { bytes = (bytes / 1024).toFixed(2) + " KB"; }
      else if (bytes > 1)           { bytes = bytes + " bytes"; }
      else if (bytes === 1)          { bytes = bytes + " byte"; }
      else                          { bytes = "0 bytes"; }
      return bytes;
    },

    closeModal(message) {
      this.isShowModal = false;
      this.replyMessage = message;
    },

    callModal() {
      this.isShowModal = true;
    },
    /* User profile */
    openUserPane() {
      if (!this.isUserPaneDisplayed)
        this.isUserPaneDisplayed = true;
    },

    closeUserPane() {
      this.userTabsActive = '';
      this.isUserPaneDisplayed = false;
    },

    async showUser(userId) {
      const isUserAlreadyDisplayed = this.userTabs.findIndex(x => x.userId === userId);
      let forceShow = false;
      if (isUserAlreadyDisplayed === -1) {
        try {
          const result = await UsersDataService.get(userId);
          if (typeof result.data === 'undefined')
            throw new Error('unable to find user '+userId);

          this.userTabs.push(UserUtils.normalize(result.data));
          forceShow = true;
        }
        catch (error) {
          window.console.error(error);
          this.$toast.error(this.$t("global_error"));
        }
      }

      this.openUserPane();

      if (forceShow || (!forceShow && this.userTabsActive !== userId)) {
        this.userTabsActive = '';
        setTimeout(() => { this.userTabsActive = userId; }, 100);
      }
    },

    showUserMedia(media, index) {
      this.userMedia.media = media;
      this.userMedia.index = index;
    },

    removeUserTab(userId) {
      const index = this.userTabs.findIndex(x => x.userId === userId);
      if (index > -1)
        this.userTabs.splice(index, 1);

      if (this.userTabs.length === 0)
        this.closeUserPane();
    }
  },

  created() {
    countriesISO.registerLocale(require("i18n-iso-countries/langs/"+this.$i18n.locale+".json"));
    this.loadTicket();
  },

  data() {
    return {
      isLoading: false,
      isSending: false,
      title: this.$t("menuitems.support.ticket") + ' ID #' + this.$route.params.ticketId,
      ticketId: this.$route.params.ticketId,
      ticket: null,
      events: [],
      lastMessage: null,
      attachments: [],
      user: null,
      device: null,
      history: [],
      page: 1,
      count: 0,
      isShowModal:false,
      attachmentsMedia: {
        media: [],
        index: -1
      },
      replyMessage: "",
      /* User profile */
      isUserPaneDisplayed: false,
      userTabs:[],
      userTabsActive: '',
      userMedia: {
        media: [],
        index: -1
      },
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" />
    <div class="position-relative">
      <b-button
          class="position-fixed"
          style="top: 77px; right: 15px;z-index:150"
          v-show="!isUserPaneDisplayed && userTabs.length > 0"
          @click="openUserPane"
          variant="outline-primary">
        <i class="bx bxs-cog"></i> Show Panel
      </b-button>

      <!-- Loader -->
      <div class="row d-flex justify-content-center">
        <vue-ellipse-progress
            v-show="isLoading"
            :loading="isLoading"
            :angle="-90"
            color="#50a5f1"
            emptyColor="#999"
            emptyColorFill="#f8f8fb"
            :size="100"
            :thickness="3"
            :emptyThickness="2"
            lineMode="out 2"
            animation="bounce 700 400"
            fontColor="#666"
            :half="false"
            class="mt-5 mb-5"
            fontSize="1rem">
        </vue-ellipse-progress>
      </div>

      <div class="row" v-if="!isLoading && ticket !== null">

      <!-- Left Col -->
      <div class="col-12 col-lg-2">

        <!-- Type -->
        <div class="card">
          <div class="card-body">
            <span
                class="badge font-size-13"
                :class="{
                        'bg-success': `${ticket.status}` === 'C',
                        'bg-warning': `${ticket.status}` === 'R',
                        'bg-danger': `${ticket.status}` === 'O',
                        'bg-info': `${ticket.status}` === 'W',
                        'bg-pink': `${ticket.status}` === 'A',
                      }"
            >{{ $t(`tickets.status_${ticket.status}`) }}</span>
            <div class="d-flex align-items-center mt-2">
              <img :src="getContactTypeIcon(ticket.ticketType)" alt="" width="24" />
              <h4 class="card-title m-0 ms-2">{{ $t(`tickets.type_${ticket.ticketType}`) }}</h4>
            </div>
          </div>
        </div>

        <!-- Event -->
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-3">{{ $t("tickets.events") }}</h4>
            <ul class="verti-timeline list-unstyled">
              <li class="event-list" v-for="(event, index) in events" :key="index">
                <div class="event-timeline-dot">
                  <i class="bx bx-right-arrow-circle"></i>
                </div>
                <div class="media">
                  <div class="avatar-xs">
                    <span
                        class="avatar-title rounded-circle font-size-15"
                        :class="{
                          'bg-light text-muted': event.senderInitials !== null,
                          'bg-info text-white ': event.senderInitials === null
                        }"
                    >{{ event.senderInitials !== null ? event.senderInitials : 'U' }}</span>
                  </div>
                  <div class="media-body">
                    <div>
                      <span
                          class="badge"
                          :class="{
                        'bg-success': `${event.message}` === 'close',
                        'bg-warning': `${event.message}` === 'review',
                        'bg-danger': `${event.message}` === 'open',
                        'bg-info': `${event.message}` === 'wait',
                        'bg-pink': `${event.message}` === 'answer',
                      }"
                      >{{ $t(`tickets.status_${event.message}`) }}</span>
                      <p
                          class="text-muted"
                      >{{ event.createdAt | moment('DD MMM YYYY HH:mm') }}</p>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <!-- Device -->
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-3">{{ $t("tickets.device") }}</h4>
            <ul class="verti-timeline list-unstyled" v-if="device != null">
              <li class="event-list">
                <div class="event-timeline-dot">
                  <i class="bx bx-right-arrow-circle"></i>
                </div>
                <div class="media">
                  <div class="me-3">
                    <i class="bx bx-mobile h2 text-primary"></i>
                  </div>
                  <div class="media-body">
                    <div>
                      <h5>{{ $t('device.brand') }}</h5>
                      <p
                          class="text-muted"
                      >{{ device.brand }}, {{ device.manufacturer }}, {{ device.model }}</p>
                    </div>
                  </div>
                </div>
              </li>
              <li class="event-list">
                <div class="event-timeline-dot">
                  <i class="bx bx-right-arrow-circle"></i>
                </div>
                <div class="media">
                  <div class="me-3">
                    <i class="bx bx-package h2 text-primary"></i>
                  </div>
                  <div class="media-body">
                    <div>
                      <h5>{{ $t('device.sdk') }}</h5>
                      <p
                          class="text-muted"
                      >{{ device.sdk }} ({{ $t("device.version") }} {{ device.version }})</p>
                    </div>
                  </div>
                </div>
              </li>
              <li class="event-list">
                <div class="event-timeline-dot">
                  <i class="bx bx-right-arrow-circle"></i>
                </div>
                <div class="media">
                  <div class="me-3">
                    <i class="bx bx-screenshot h2 text-primary"></i>
                  </div>
                  <div class="media-body">
                    <div>
                      <h5>{{ $t('device.screen') }}</h5>
                      <p
                          class="text-muted"
                      >{{ device.screen.width }} * {{ device.screen.height }}<br>{{ $t("device.density") }} {{ device.screen.density }}<br>{{ device.screen.densityDpi }}dpi</p>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

      </div>

      <!-- Center Col -->
      <div class="col-12 col-lg-6">

        <div class="card">
          <div class="card-body">

            <!-- USER -->
            <div v-if="user != null">
              <div class="media mb-4 cursor-pointer" @click="showUser(user.userId)" v-if="user.userId !== ''">
                <img
                    class="d-flex rounded-circle avatar-lg"
                    @click="showAttachment(`${user.userId}`)"
                    id="ticket-userImg"
                    :src="require('@/assets/images/users/default-user.png')"
                    alt=""
                />
                <div class="media-body ms-3">
                  <h5 class="font-size-14 mt-1">{{ user.name }} {{ getAge(user.birth) }} <img :src="getGenderImg(user.sex)" width="16" height="16" alt=""/></h5>
                  <p class="mb-1 font-size-12">
                    <country-flag :country='user.country' size='small'/>
                    {{ user.locality }}, {{ getCountry }}
                  </p>
                  <small class="text-muted">{{ ticket.email }}</small><br>
                  <small class="text-muted">#{{ user.userId }}</small>
                </div>
              </div>
              <div class="media mb-4" v-else>
                <div class="avatar-md">
                  <img :src="require('@/assets/images/users/default-user.png')" class="d-flex rounded-circle avatar-md" />
                </div>
                <div class="media-body ms-3">
                  <h5 class="font-size-14 mt-1">{{ user.name }}</h5>
                  <small class="text-muted">{{ user.email }}</small>
                </div>
              </div>
            </div>

            <!-- LAST MESSAGE -->
            <div class="mb-5 mt-3" v-if="lastMessage !== null">
              <h4 class="card-title mb-4">{{ $t("tickets.message") }}</h4>
              <div class="media">
                <div class="avatar-xs">
                      <span
                          class="avatar-title rounded-circle font-size-15"
                          :class="{
                            'bg-light text-muted': lastMessage.senderInitials !== null,
                            'bg-info text-white': lastMessage.senderInitials === null
                          }"
                      >{{ lastMessage.senderInitials !== null ? lastMessage.senderInitials : 'U' }}</span>
                </div>
                <div class="media-body ms-2">
                  <p class="text-dark font-size-13">
                    <strong>{{ lastMessage.senderName !== null ? lastMessage.senderName : user.name }}</strong><br>
                    <small class="text-primary">{{ lastMessage.createdAt | moment('DD MMM YYYY HH:mm') }}</small>
                  </p>
                  <p class="text-muted font-size-15">{{ lastMessage.message }}</p>
                </div>
              </div>
            </div>

            <hr>

            <!-- Attachments -->
            <div>
              <h4 class="card-title mt-4 mb-3">{{ $t("tickets.attachments") }}</h4>
              <p class="text-muted font-size-14"><i class="mdi mdi-paperclip"></i>{{ attachments.length }} {{ $t("tickets.attachments_found") }}</p>
              <div class="d-inline-flex justify-content-start" v-for="attachment in attachments" :key="attachment.fileName">
                <div class="bg-light rounded-5 p-2 m-2">
                  <img
                      @click="showAttachment(`${attachment.fileName}`)"
                      style="height:75px;"
                      class="d-inline-block"
                      :id="'attachment-' + attachment.fileName"
                    />
                  <span class="d-inline-block ms-1 font-size-12">
                    <small>{{ attachment.fileName }}</small><br>
                    <b>{{ formatSizeUnits(attachment.fileSize) }}</b>
                  </span>
                </div>
              </div>
            </div>

            <hr>

            <!-- Button -->
            <div class="card-footer bg-white" v-if="ticket.status !== 'C'">
              <div class="d-flex justify-content-end">
                <b-button variant="primary" class="" @click="replyTicket()">{{ $t("reply") }}</b-button>
                <b-button variant="danger" class="ms-4" @click="closeTicket()">{{ $t("close") }}</b-button>
              </div>
            </div>
          </div>
        </div>

      </div>

      <!-- Right Col -->
      <div class="col-12 col-lg-4">

        <!-- HISTORY -->
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-3">{{ $t("tickets.history") }}</h4>
            <div v-if="history.length > 0">
              <ul class="verti-timeline list-unstyled">
                <li class="event-list" v-for="(msg, index) in history" :key="index">
                  <div class="event-timeline-dot">
                    <i class="bx bx-right-arrow-circle"></i>
                  </div>
                  <div class="media">
                    <div class="avatar-xs">
                    <span
                        class="avatar-title rounded-circle font-size-15"
                        :class="{
                          'bg-light text-muted': msg.senderInitials !== null,
                          'bg-info text-white': msg.senderInitials === null
                        }"
                    >{{ msg.senderInitials !== null ? msg.senderInitials : 'U' }}</span>
                    </div>
                    <div class="media-body ms-2">
                      <p class="text-dark font-size-13">
                        <strong>{{ msg.senderName !== null ? msg.senderName : user.name }}</strong><br>
                        <small class="text-primary">{{ msg.createdAt | moment('DD MMM YYYY HH:mm') }}</small>
                      </p>
                      <p class="text-muted font-size-15">{{ msg.message }}</p>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
            <p v-else class="text-muted font-size-14">{{ $t("tickets.no_history") }}</p>
          </div>
        </div>

      </div>

    </div>

    </div>

    <b-sidebar id="sidebar-right" right v-model="isUserPaneDisplayed">
      <vue-tabs
          v-model="userTabsActive"
          active-tab-color="#213213"
          active-text-color="white">
        <v-tab v-for="userTab in userTabs" :key="userTab.userId" :title="userTab.userId" >
          <div slot="title">{{ userTab.name }} <i @click.stop="removeUserTab(userTab.userId)" class="bx bx-x-circle" ></i></div>
          <UserProfile
              :userId="null"
              :user="userTab"
              v-on:SHOW_MEDIA="showUserMedia">
          </UserProfile>
        </v-tab>
      </vue-tabs>
    </b-sidebar>

    <!-- reply modal -->
    <ModalWriteUser
        :user="user"
        :visible="isShowModal"
        :msg="replyMessage"
        :sending="isSending"
        @closeModalWriteUser="closeModal"
        @sendMessageWriteUser="sendReply"
    >
    </ModalWriteUser>

    <!-- LIGHTBOX -->
    <CoolLightBox
        :items="attachmentsMedia.media"
        :index="attachmentsMedia.index"
        :effect="'fade'"
        @close="attachmentsMedia.index = null">
    </CoolLightBox>
  </Layout>
</template>

<style>
  .event-list {
    padding: 0 0 30px 15px !important;
  }

  .event-list .media-body {
    margin-left: 10px;
  }

  .cursor-pointer {
    cursor: pointer;
  }

  .b-sidebar-right {
    width:35%;
  }

  .b-sidebar-body {
    padding:16px;
  }
</style>